import Component from "@ember/component"
import { action, computed, set } from "@ember/object"
import { inject as service } from "@ember/service"
import { tracked } from "@glimmer/tracking"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import type ModalManager from "cc-frontend/services/modal-manager"
import type Session from "cc-frontend/services/session"
import _ from "lodash"
import moment from "moment"
import styles from "./day.module.css"

export default class Day extends Component {
  readonly styles = styles

  @service declare modalManager: ModalManager
  @service declare session: Session
  // Args
  owner!: Record<string, any>
  planbook!: Planbook
  rotationCalendar!: RotationCalendar
  setNewDate!: Function
  date!: string
  courses!: Array<Course>
  visibleCourses!: Array<Course>
  isFilteringClasses!: boolean
  lineUpClasses!: boolean
  lessonsCollapsed!: boolean
  daysAsRows!: boolean
  classesLinedUp!: boolean
  hiddenCourseIds: Array<string> = []
  unitId!: string

  // properties
  sortableChildren = []
  hoveringDateString = null
  @tracked isDayMenuOpen = false

  //Services
  @service("ember-store") store
  @service appState

  addSortableChild(child) {
    // @ts-ignore
    this.sortableChildren.addObject(child)
  }

  removeSortableChild(child) {
    // @ts-ignore
    this.sortableChildren.removeObject(child)
  }

  // Computed Properties
  // If it's the week view, we replace. Otherwise, we append
  @computed("date", "rotationCalendar.attributes.schoolDays")
  get dateString() {
    let date = this.date
    // Need to make sure it's on a school date
    return moment(date).format("YYYY-MM-DD")
  }

  @action
  page(direction) {
    const validSchoolDays = this.rotationCalendar.attributes.schoolDays
    const op = direction === "back" ? "subtract" : "add"

    const newDate = moment(this.date)
    do {
      newDate[op](1, "day")
    } while (!validSchoolDays.includes(newDate.weekday()))

    this.setNewDate(newDate.format("YYYY-MM-DD"))
  }

  @action
  hoverOnDate(dateString) {
    set(this, "hoveringDateString", dateString)
  }

  @action
  printOrDownload() {
    if (this.session.isAuthenticated !== true) {
      this.session.mustSignInToProceed("Print/Download Day")
      return
    }

    if (_.includes(this.session.user.content.meta.permissions, "can-print-day")) {
      this.modalManager.openModal("planner/modals/print-download", {
        courseIds: _.chain(this.courses)
          .map((course) => course.id)
          .reject((courseId) => _.includes(this.hiddenCourseIds, courseId))
          .value(),
        planbookId: this.planbook.id,
        startDate: this.dateString,
        endDate: this.dateString,
        layout: "planbook-date:single",
        currentUserId: this.session.userId,
        isFilteringClasses: this.isFilteringClasses,
        canPrint: _.includes(this.session.user.content.meta.permissions, "can-print-day"),
        dateRangeString: moment(this.dateString).format("dddd, MMMM Do YYYY"),
        unitId: this.unitId,
      })
    } else {
      this.modalManager.openModal("planner/modals/print-paywall", {})
    }
  }

  @action toggleDayMenu() {
    this.isDayMenuOpen = !this.isDayMenuOpen
  }
}
